import { useState } from 'react';
import styles from './RequestTypeForms.module.scss';
import { statesWithId } from '../../../../components/body/support/forms/shared/FormsData';
import {
  normalizeInput,
  validateCode,
} from '../../../../components/body/checkout/sections/checkoutUtils';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../src/app/hooks';
import { submitSupportTicket } from '../../../../src/features/user/userState';
import { validationTypes } from '../../../../commons/validations';
import { getValidationMessage } from '../../../../commons/utils';
import {
  cleanNameField,
  processNameField,
  phoneNumberLengthValidation,
  addressFormater,
  processCityField,
  processEmailField,
  cleanEmailField,
} from '../../../../commons/formatters';
import {
  removeCountryCodeAndFormat,
  setFormDataFieldValue,
} from '../../../../commons/utils/utils';
import {
  ADD1_REGISTER,
  ADD2_REGISTER,
  CITY_REGISTER,
  COMMENTS_REGISTER,
  EMAIL_REGISTER,
  FN_REGISTER,
  LN_REGISTER,
  PHONE_REGISTER,
  STATE_REGISTER,
  ZIP_REGISTER,
} from '../../../../commons/Constants';
import {
  ContainedButton,
  CountryDropdown,
  TextInput,
} from '../../../../commons/hoc';
import TextAreaInput from '../../../../commons/hoc/Input/TextAreaInput/TextAreaInput';
import { BaseAddress } from '../../checkout/components';

const ChangeAddressForm = ({ currentOption, setFormSubmitted = () => {} }) => {
  const dispatch = useAppDispatch();

  const {
    register,
    formState,
    formState: { errors },
    handleSubmit,
    clearErrors,
    trigger,
    getValues,
    setValue,
  } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });
  const onSubmit = (data) => {
    dispatch(
      submitSupportTicket({
        'qmonitorid': 31,
        'firstname': data.firstName,
        'lastname': data.lastName,
        'phone': removeCountryCodeAndFormat(data.phone, true),
        'email': data.email,
        'Custom[638]': data.address1,
        'Custom[639]': data.address2,
        'Custom[640]': data.city,
        'Custom[642]': statesWithId[data.state].alias,
        'Custom[643]': data.zip,
        'Custom[620]': data.newAddress1,
        'Custom[621]': data.newAddress2,
        'Custom[622]': data.newCity,
        'Custom[623]': data.newState.substring(0, 2),
        'Custom[624]': data.newZip,
        'Custom[625]': removeCountryCodeAndFormat(data.newPhone, true),
        ...(data.comments && { message: data.comments }),
      })
    );
    setFormSubmitted(true);
  };

  const [phoneNumber, setPhoneNumber] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [newZipCode, setNewZipCode] = useState('');
  statesWithId[0] = { ...statesWithId[0], alias: '', disabled: false };
  const [currentAddress, setCurrentAddress] = useState({});

  const handleInputOnChange = (key, value) => {
    setValue(key, value, { shouldValidate: false, shouldTouch: true });
    clearErrors(key);
  };

  const handleAutoComplete = (e) => {
    if (currentOption === 'address') {
      let target = e?.target?.form ? Array.from(e.target.form) : [];
      let tempFormData = {};
      target.forEach((item) => {
        if (item.name !== '') {
          tempFormData[item.name] =
            item.name === PHONE_REGISTER
              ? normalizeInput(item.value)
              : item.value;
        }
      });
      setFormDataFieldValue(getValues(), tempFormData, setValue, trigger);
      setPhoneNumber(getValues().phone);
      setZipCode(getValues().zip);
      clearErrors();
    }
  };

  const handleAddressChange = (address, key) => {
    setValue(key, address, { shouldDirty: true });
  };

  const firstName = register(FN_REGISTER, {
    ...validationTypes.firstName,
    onChange: (e) => {
      handleInputOnChange(FN_REGISTER, processNameField(e.target.value));
      clearErrors(FN_REGISTER);
    },
    onBlur: (e) => {
      handleInputOnChange(FN_REGISTER, cleanNameField(e.target.value));
    },
  });

  const lastName = register(LN_REGISTER, {
    ...validationTypes.lastName,
    onChange: (e) => {
      handleInputOnChange(LN_REGISTER, processNameField(e.target.value));
      clearErrors(LN_REGISTER);
    },
    onBlur: (e) => {
      handleInputOnChange(LN_REGISTER, cleanNameField(e.target.value));
    },
  });

  const address1 = register(ADD1_REGISTER, {
    ...validationTypes.address1,
    onChange: (e) => {
      handleAddressChange(addressFormater(e.target.value), ADD1_REGISTER);
      clearErrors(ADD1_REGISTER);
    },
    onBlur: (e) => {
      handleAddressChange(
        addressFormater(e.target.value.trim()),
        ADD1_REGISTER
      );
    },
  });

  const address2 = register(ADD2_REGISTER, {
    ...validationTypes.address2,
    onChange: (e) => {
      handleAddressChange(addressFormater(e.target.value), ADD2_REGISTER);
      clearErrors(ADD2_REGISTER);
    },
    onBlur: (e) => {
      handleAddressChange(
        addressFormater(e.target.value.trim()),
        ADD2_REGISTER
      );
    },
  });

  const city = register(CITY_REGISTER, {
    ...validationTypes.city,
    onChange: (e) => {
      handleInputOnChange(CITY_REGISTER, processCityField(e.target.value));
      clearErrors(CITY_REGISTER);
    },
    onBlur: (e) => {
      handleInputOnChange(CITY_REGISTER, e.target.value.trim());
    },
  });

  const zip = register(ZIP_REGISTER, {
    ...validationTypes.zip,
    onChange: (e) => {
      handleInputOnChange(ZIP_REGISTER, e.target.value);
      setZipCode(validateCode(e.target.value, zipCode, 5));
      clearErrors(ZIP_REGISTER);
    },
  });

  const phone = register(PHONE_REGISTER, {
    ...validationTypes.phone,
    onChange: (e) => {
      clearErrors(PHONE_REGISTER);
      let number = phoneNumberLengthValidation(e.target.value);
      if (number !== null) {
        setPhoneNumber(number);
      }
    },
    onBlur: (e) => {
      setPhoneNumber(normalizeInput(e.target.value, phoneNumber));
    },
  });

  const email = register(EMAIL_REGISTER, {
    ...validationTypes.email,
    onChange: (e) => {
      handleInputOnChange(EMAIL_REGISTER, processEmailField(e.target.value));
      clearErrors(EMAIL_REGISTER);
    },
    onBlur: (e) => {
      handleInputOnChange(EMAIL_REGISTER, cleanEmailField(e.target.value));
    },
  });

  const newAddress1 = register('newAddress1', validationTypes.address1);

  const newAddress2 = register('newAddress2', validationTypes.address2);

  const newCity = register('newCity', validationTypes.city);

  const newZip = register('newZip', validationTypes.zip);

  const newPhone = register('newPhone', {
    ...validationTypes.phone,
    onBlur: (e) => {
      setNewPhoneNumber(normalizeInput(e.target.value, phoneNumber));
    },
    onChange: (e) => {
      let number = phoneNumberLengthValidation(e.target.value);
      if (number !== null) {
        setNewPhoneNumber(number);
      }
      clearErrors(PHONE_REGISTER);
    },
  });
  const handlePhoneNumber = (event) => {
    setPhoneNumber(event.target.value.replace(/\D/g, ''));
  };

  const handleNewPhoneNumber = (event) => {
    setNewPhoneNumber(event.target.value.replace(/\D/g, ''));
  };
  const comments = register(COMMENTS_REGISTER, {
    required: false,
  });

  return (
    <div className={styles.supportFormsContainer}>
      <form onSubmit={handleSubmit(onSubmit)} id="form-address">
        {/* For safer side - Don't Delete below commented code */}
        {/* <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              label="First Name"
              required={validationTypes.firstName.required}
              register={firstName}
              maxLength={validationTypes.firstName.maxLength}
              errors={errors}
              placeholder="First Name"
            />
          </div>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              label="Last Name"
              required={validationTypes.lastName.required}
              register={lastName}
              maxLength={validationTypes.lastName.maxLength}
              errors={errors}
              placeholder="Last Name"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              type="tel"
              inputMode="numeric"
              register={phone}
              required={validationTypes.phone.required}
              label="Phone Number"
              placeholder="(XXX) XXX-XXXX"
              onFocus={handlePhoneNumber}
              onClickCapture={handlePhoneNumber}
              errors={errors}
              maxLength={validationTypes.phone.maxLength}
              value={phoneNumber}
            />
          </div>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              required={validationTypes.email.required}
              register={email}
              label="Email Address"
              maxLength={validationTypes.email.maxLength}
              errors={errors}
              placeholder="Email Address"
            />
          </div>
        </div>

        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.address1.required}
              register={address1}
              label="Address - Line 1"
              maxLength={validationTypes.address1.maxLength}
              errors={errors}
              placeholder="Address - Line 1"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              register={address2}
              label="Address - Line 2"
              maxLength={validationTypes.address2.maxLength}
              errors={errors}
              placeholder="Address - Line 2"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.city.required}
              register={city}
              label="City"
              maxLength={validationTypes.city.maxLength}
              errors={errors}
              placeholder="City"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <label>State*</label>
            <CountryDropdown
              id="restartStateId"
              name={STATE_REGISTER}
              // label={'State'}
              tkey="title"
              options={Object.values(statesWithId)}
              {...register(STATE_REGISTER, { required: true })}
              optionsOrder={[1, 2]}
              required={validationTypes.state.required}
              onChange={(e) =>
                handleInputOnChange(STATE_REGISTER, e.target.value)
              }
              // errorType={STATE}
              // error={errors[formKeyMap[STATE]]?.type ? true : false}
            />
            {getValidationMessage(errors, STATE_REGISTER)}
          </div>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              label="Zip Code"
              required={validationTypes.zip.required}
              register={zip}
              maxLength={validationTypes.zip.maxLength}
              errors={errors}
              placeholder="Zip Code"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.address1.required}
              register={newAddress1}
              label="New Address - Line 1"
              maxLength={validationTypes.address1.maxLength}
              //   errors={errors}
              placeholder="New Address - Line 1"
              onChange={(e) => {
                newAddress1.onChange(e);
                handleAddressChange(
                  addressFormater(e.target.value),
                  'newAddress1'
                );
                clearErrors('newAddress1');
              }}
            />
            {getValidationMessage(errors, 'newAddress1', 'address1')}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              register={newAddress2}
              label="New Address - Line 2"
              maxLength={validationTypes.address2.maxLength}
              placeholder="New Address - Line 2"
              onChange={(e) => {
                newAddress2.onChange(e);
                handleAddressChange(
                  addressFormater(e.target.value),
                  'newAddress2'
                );
                clearErrors('newAddress2');
              }}
            />
            {getValidationMessage(errors, 'newAddress2', 'address2')}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.city.required}
              register={newCity}
              label="New City"
              maxLength={validationTypes.city.maxLength}
              placeholder="New City"
            />
            {getValidationMessage(errors, 'newCity', 'city')}
          </div>
        </div>

        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <label>New State*</label>
            <CountryDropdown
              id="addressNewStateId"
              name="newState"
              // label={'State'}
              tkey="title"
              pkey="alias"
              options={Object.values(statesWithId)}
              {...register('newState', { required: true })}
              optionsOrder={[1, 2]}
              required={validationTypes.state.required}
              onChange={(e) => handleInputOnChange('newState', e.target.value)}
            />
            {getValidationMessage(errors, 'newState', STATE_REGISTER)}
          </div>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              label="New Zip Code"
              required={validationTypes.zip.required}
              register={newZip}
              maxLength={validationTypes.zip.maxLength}
              onChange={(e) => {
                newZip.onChange(e);
                clearErrors('newZip');
                setNewZipCode(validateCode(e.target.value, newZipCode, 5));
              }}
              placeholder="New Zip Code"
              value={newZipCode}
            />
            {getValidationMessage(errors, 'newZip', ZIP_REGISTER)}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              type="tel"
              inputMode="numeric"
              register={newPhone}
              required={validationTypes.phone.required}
              label="New Phone Number"
              placeholder="(XXX) XXX-XXXX"
              onFocus={handlePhoneNumber}
              onClickCapture={handlePhoneNumber}
              maxLength={validationTypes.phone.maxLength}
              value={newPhoneNumber}
            />
            {getValidationMessage(errors, 'newPhone', PHONE_REGISTER)}
          </div>
        </div> */}
        <BaseAddress
          key=""
          id=""
          isSupportPage={true}
          rdxUpdateAddressCB={(address) => setCurrentAddress(address)}
          hookFormCB={{
            register,
            formState,
            handleSubmit,
            clearErrors,
            trigger,
            getValues,
            setValue,
          }}
          data={currentAddress}
        />
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.email.required}
              register={email}
              label="Email Address"
              isDarkBorder={true}
              maxLength={validationTypes.email.maxLength}
              errors={errors}
              placeholder="Email Address"
            />
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.address1.required}
              register={newAddress1}
              isDarkBorder={true}
              label="New Address - Line 1"
              maxLength={validationTypes.address1.maxLength}
              //   errors={errors}
              placeholder="New Address - Line 1"
              onChange={(e) => {
                newAddress1.onChange(e);
                handleAddressChange(
                  addressFormater(e.target.value),
                  'newAddress1'
                );
                clearErrors('newAddress1');
              }}
            />
            {getValidationMessage(errors, 'newAddress1', 'address1')}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              register={newAddress2}
              label="New Address - Line 2"
              isDarkBorder={true}
              maxLength={validationTypes.address2.maxLength}
              placeholder="New Address - Line 2"
              onChange={(e) => {
                newAddress2.onChange(e);
                handleAddressChange(
                  addressFormater(e.target.value),
                  'newAddress2'
                );
                clearErrors('newAddress2');
              }}
            />
            {getValidationMessage(errors, 'newAddress2', 'address2')}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerSingle}>
            <TextInput
              required={validationTypes.city.required}
              register={newCity}
              isDarkBorder={true}
              label="New City"
              maxLength={validationTypes.city.maxLength}
              placeholder="New City"
            />
            {getValidationMessage(errors, 'newCity', 'city')}
          </div>
        </div>

        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <label>New State*</label>
            <CountryDropdown
              id="addressNewStateId"
              name="newState"
              // label={'State'}
              tkey="title"
              isDarkBorder={true}
              pkey="alias"
              options={Object.values(statesWithId)}
              {...register('newState', { required: true })}
              optionsOrder={[1, 2]}
              required={validationTypes.state.required}
              onChange={(e) => handleInputOnChange('newState', e.target.value)}
            />
            {getValidationMessage(errors, 'newState', STATE_REGISTER)}
          </div>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              label="New Zip Code"
              required={validationTypes.zip.required}
              register={newZip}
              maxLength={validationTypes.zip.maxLength}
              isDarkBorder={true}
              onChange={(e) => {
                newZip.onChange(e);
                clearErrors('newZip');
                setNewZipCode(validateCode(e.target.value, newZipCode, 5));
              }}
              placeholder="New Zip Code"
              value={newZipCode}
            />
            {getValidationMessage(errors, 'newZip', ZIP_REGISTER)}
          </div>
        </div>
        <div className={styles.inputRowContainer}>
          <div className={styles.inputBoxContainerDouble}>
            <TextInput
              type="tel"
              inputMode="numeric"
              register={newPhone}
              isDarkBorder={true}
              required={validationTypes.phone.required}
              label="New Phone Number"
              placeholder="(XXX) XXX-XXXX"
              onFocus={handlePhoneNumber}
              onClickCapture={handlePhoneNumber}
              maxLength={validationTypes.phone.maxLength}
              value={newPhoneNumber}
            />
            {getValidationMessage(errors, 'newPhone', PHONE_REGISTER)}
          </div>
        </div>
        <div className={styles.questionsCommentsContainer}>
          <label>
            Comments/Questions
            {/* <div className={styles.fieldRequired}></div> */}
          </label>
          <div className={styles.questionsComments}>
            <TextAreaInput
              className={styles.questionTextArea}
              id="questionsComments"
              minRows={4}
              isDarkBorder={true}
              onChange={(e) => {
                clearErrors(COMMENTS_REGISTER);
              }}
              register={comments}
            />
            {/* {getValidationMessage(errors,COMMENTS_REGISTER)} */}
          </div>
          <div className={styles.sendButtonContainer}>
            <ContainedButton
              btnType="dark"
              className={styles.submitRequestButton}
            >
              Submit Request
            </ContainedButton>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChangeAddressForm;
